// SortDropdown component - Styled dropdown for sort options
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const SortDropdown = ({ value, onChange, shopperLocation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const sortOptions = [
    { value: 'pain-asc', label: 'Best Value (Price×Distance)' },
    { value: 'price-asc', label: 'Price: Low to High' },
    { value: 'price-desc', label: 'Price: High to Low' },
    { value: 'distance-asc', label: 'Distance: Closest First' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
  };

  const currentOption = sortOptions.find(option => option.value === value) || sortOptions[0];
  const shouldShowLocationMessage = value === 'distance-asc' || value === 'pain-asc';

  const getLocationDisplayName = () => {
    if (!shopperLocation) return 'your location';
    if (shopperLocation.formatted_address) return shopperLocation.formatted_address;
    if (shopperLocation.city) {
      return `${shopperLocation.city}${shopperLocation.state ? `, ${shopperLocation.state}` : ''}`;
    }
    if (shopperLocation.lat && shopperLocation.lng) {
      return `coordinates (${parseFloat(shopperLocation.lat).toFixed(2)}, ${parseFloat(shopperLocation.lng).toFixed(2)})`;
    }
    return 'your location';
  };

  return (
    <div className="sort-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className={`sort-dropdown-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="dropdown-text">{currentOption.label}</span>
        <span className="dropdown-arrow">
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="sort-dropdown-menu" role="listbox">
          <div className="dropdown-content">
            {sortOptions.map((option) => (
              <div
                key={option.value}
                className={`sort-option ${option.value === value ? 'selected' : ''}`}
                onClick={() => handleOptionClick(option.value)}
                role="option"
                aria-selected={option.value === value}
              >
                <span className="option-label">{option.label}</span>
                {option.value === value && <span className="selected-indicator">✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {shouldShowLocationMessage && shopperLocation && (
        <div className="location-info">
          Showing distances from {getLocationDisplayName()}
        </div>
      )}
    </div>
  );
};

SortDropdown.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  shopperLocation: PropTypes.object
};

export default SortDropdown;
